import type { ReactElement, ReactNode } from "react"
import ModalItemMol from "@onestore/hel/dist/components/molecules/ModalItemMol"
import { StyledLabelAtm } from "@onestore/hel/dist/components/molecules/ModalItemMol/ModalItemMol.styled"
import { CustomTextColor } from "@onestore/hel/dist/utils/getCustomColor"
import _t from "@core/i18n"
import type { UsePrices } from "@core/period-info"
import { PeriodInfo } from "@core/period-info"
import { PriceType } from "@core/types"
import type { CloudBluePeriod } from "@gatsby-plugin-definitions/fragments/CloudBluePeriod"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import BaseMarkdown from "@gatsby-plugin-generic-page/components/Markdown/Base"
import { CustomTextColors } from "@gatsby-plugin-generic-page/fragments/customColor"
import type { ModalItemFlatData } from "@gatsby-plugin-generic-page/fragments/modalItem"
import { ModalItemProductType } from "@gatsby-plugin-generic-page/fragments/modalItem"
import getBonusUrl from "@gatsby-plugin-generic-page/helpers/getBonusUrl"
import getLinkProps from "@gatsby-plugin-generic-page/helpers/getLinkProps"
import {
  getStaticLowestPrice,
  getStaticPrices,
} from "@gatsby-plugin-generic-page/helpers/staticPrices"
import { Text } from "@ionos-web-design-system/react"
import { typeMatches } from "@onestore-graphql"
import { omit } from "lodash"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import { sendGaEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"

type LinkElement = {
  href?: string
  to?: string
  title: string
  openInNewTab?: boolean
}

export type ModalItemProps = ModalItemFlatData & {
  categoryName: string
}

export default function ModalItem({
  title,
  description,
  productType,
  productSaleConfiguration,
  domain,
  staticPrice,
  prefixAlternativeText,
  alternativeText,
  link,
  priceBoxBackgroundColor,
  priceBoxTextColor,
  categoryName,
}: ModalItemProps): ReactElement<ModalItemProps> | null {
  const { currentPriceType, isNettoPriceType } = usePriceTypeContext()
  const externalLink = !isEmpty(link)
  const hasProduct =
    typeMatches(productType, ModalItemProductType.PLAN) &&
    !isEmpty(productSaleConfiguration) &&
    !isEmpty(productSaleConfiguration[0].flatData)
  const hasDomain =
    typeMatches(productType, ModalItemProductType.DOMAIN) && !isEmpty(domain)
  const hasStaticPrice =
    typeMatches(productType, ModalItemProductType.STATIC_PRICE) &&
    !isEmpty(staticPrice)
  const hasIndividualPrice =
    typeMatches(productType, ModalItemProductType.INDIVIDUAL_PRICE) &&
    !isEmpty(alternativeText)
  let period: CloudBluePeriod | undefined
  let linkElement: LinkElement = {
    href: "",
    to: "",
    title: "",
    openInNewTab: false,
  }
  let product: CloudBluePlan
  let prices:
    | Omit<UsePrices, "renewPrice" | "oldPriceValue" | "promotionPercent">
    | undefined
  let periodText: string = ""
  let alternativeContent: ReactNode

  if (hasProduct) {
    product = productSaleConfiguration[0].flatData.plan[0]
    period = product?.flatData.periods.find((period) => {
      if (!isEmpty(productSaleConfiguration[0].flatData.period)) {
        return (
          period.period_name ===
          productSaleConfiguration[0].flatData.period.period
        )
      } else {
        return period.default
      }
    })
  } else if (hasDomain) {
    period = domain[0].flatData.periods.find((period) => period.default)
  } else if (hasStaticPrice) {
    const staticRegisterPrice = getStaticPrices(staticPrice, currentPriceType)
    const staticLowestPrice = getStaticLowestPrice(
      staticPrice,
      currentPriceType
    )

    const periodName = Object.keys(staticRegisterPrice)[0]

    const priceValue = staticRegisterPrice[periodName].price.value

    const lowestPrice = staticLowestPrice
      ? staticLowestPrice[periodName].price
      : priceValue

    const lowestPricePercent = staticLowestPrice
      ? staticLowestPrice[periodName].percent
      : "0%"

    prices = {
      priceValue,
      lowestPrice,
      lowestPricePercent,
    }

    periodText = staticRegisterPrice[periodName].price.suffix
  } else if (hasIndividualPrice) {
    alternativeContent = (
      <>
        <Text variant="heading2xl" className="text-white">
          {`${prefixAlternativeText} `}

          <Text variant="bodyXs" className="text-white">
            <BaseMarkdown>{alternativeText}</BaseMarkdown>
          </Text>
        </Text>
      </>
    )
  }

  if (hasProduct || hasDomain) {
    const periodInfo = period ? new PeriodInfo(period) : undefined

    const periodPrices = periodInfo
      ? periodInfo.getPrices(
          isNettoPriceType ? PriceType.NETTO : PriceType.GROSS
        )
      : undefined

    const priceTypeText = isNettoPriceType
      ? _t("prices.netto")
      : _t("prices.gross")

    const periodInfoText = periodInfo
      ? `${priceTypeText} ${periodInfo.periodText()}`
      : ""

    prices = {
      priceValue: periodPrices?.priceValue || "",
      lowestPrice: periodPrices?.lowestPrice || "",
      lowestPricePercent: periodPrices?.lowestPricePercent || "",
    }

    periodText = periodInfoText
  }

  const lowestPriceText = !isEmpty(prices) ? (
    <>
      {_t("prices.lowerPriceText") + " "}
      <Text variant="bodyXs" className="whitespace-nowrap">
        {prices.lowestPrice}
        <StyledLabelAtm variant="dark" size="small" htmlTag="span">
          {prices.lowestPricePercent}
        </StyledLabelAtm>
      </Text>
    </>
  ) : null

  if (externalLink) {
    linkElement = omit(getLinkProps(link, currentPriceType), [
      "text",
      "onClick",
    ])
  } else if (hasProduct) {
    linkElement.href = getBonusUrl(
      productSaleConfiguration[0].flatData,
      productSaleConfiguration[0].url,
      currentPriceType
    )
    linkElement.title = title
  }

  const hasDarkText = !isEmpty(priceBoxTextColor)
    ? typeMatches(CustomTextColors[priceBoxTextColor], CustomTextColor.DARK)
    : false

  return (
    <ModalItemMol
      title={title}
      description={description}
      pricePrefix={_t("prices.promotion")}
      price={prices?.priceValue || ""}
      priceSuffix={periodText}
      color={
        !isEmpty(priceBoxBackgroundColor)
          ? priceBoxBackgroundColor.color
          : "#ffffff"
      }
      lowestPrice={lowestPriceText}
      alternativeContent={alternativeContent}
      link={{
        href: linkElement.href,
        to: linkElement.to,
        title: linkElement.title,
        openInNewTab: linkElement.openInNewTab,
      }}
      onClick={() =>
        sendGaEvent({ eventCategory: categoryName, eventAction: title })
      }
      externalLink={externalLink}
      hasDarkText={hasDarkText}
    />
  )
}
